import { PaginatedChristmasBonusQueryVariables, ChristmasBonusSortField } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import SortDropdownFilter from "../../Shared/SortDropdownFilter";

interface Props {
  displayAmount: number;
  handleSubmitClick: (_variables: PaginatedChristmasBonusQueryVariables) => void;
}

/**
 * ChristmasBonusSortingDropdown component
 * Provides a dropdown menu for sorting christmas bonus data by different fields and orders
 *
 * @param {Object} props - Component props
 * @param {number} props.displayAmount - Number of items to display per page
 * @param {Function} props.handleSubmitClick - Function to handle submitting the updated query variables
 * @returns {JSX.Element} Sorting dropdown component
 */
const ChristmasBonusSortingDropdown = (props: Props) => {
  const { t } = useTranslation(["translation", "common", "components"]);

  /**
   * Gets user-friendly names for sort fields
   * @param {ChristmasBonusSortField} field - The sort field to get a display name for
   * @returns {string} User-friendly name for the sort field
   */
  const getSortFieldName = (field: ChristmasBonusSortField): string => {
    switch (field) {
    case ChristmasBonusSortField.BonusRelative:
      return t("components:filters.christmasbonus.sortfields.bonusrelative");
    case ChristmasBonusSortField.BonusAbsolute:
      return t("components:filters.christmasbonus.sortfields.bonusabsolute");
    case ChristmasBonusSortField.ActiveFrom:
      return t("components:filters.christmasbonus.sortfields.activefrom");
    case ChristmasBonusSortField.Province:
      return t("components:filters.christmasbonus.sortfields.province");
    default:
      return field;
    }
  };

  const christmasBonusSortConfig = {
    paginationStateKey: "paginatedChristmasBonus" as keyof PaginationState,
    sortFields: ChristmasBonusSortField,
    getSortFieldName,
    defaultSortField: ChristmasBonusSortField.ActiveFrom,
    buildQueryVariables: (displayAmount: number, filters: any, sort: { field: ChristmasBonusSortField; order: any } | null): PaginatedChristmasBonusQueryVariables => ({
      first: displayAmount,
      filters: {
        activeFrom: filters.activeFrom,
        bonusRelative: filters.bonusRelative,
        bonusAbsolute: filters.bonusAbsolute,
        provinces: filters.provinces,
      },
      sort: sort
    }),
    extractFilters: (paginationState: any) => ({
      activeFrom: paginationState.filters?.activeFrom,
      bonusRelative: paginationState.filters?.bonusRelative,
      bonusAbsolute: paginationState.filters?.bonusAbsolute,
      provinces: paginationState.filters?.provinces,
    })
  };

  return (
    <SortDropdownFilter
      displayAmount={props.displayAmount}
      handleSubmitClick={props.handleSubmitClick}
      config={christmasBonusSortConfig}
    />
  );
};

export default ChristmasBonusSortingDropdown;
